import React, { useState } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Flex, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DonutTooltip } from "./DonutTooltip";
import {
  arcSegment,
  centerLabel,
  centerStack,
  centerTotal,
  donutRoot,
  donutSizeVar,
  svg,
} from "./styles_vanilla.css";
import { buildArcPath, polarToCartesian } from "./utils";
import type { DonutChartProps, HoveredState } from "./types";

export type { DonutSegment } from "./types";

const EMPTY_COLOR = Colors.Neutral300;
const TOOLTIP_GAP = 5;
const SEGMENT_GAP_PX = 2;

export const DonutChart: React.FC<DonutChartProps> = ({
  segments,
  totalCount,
  totalLabel,
  size = 190,
  strokeWidth = 28,
}) => {
  const [hovered, setHovered] = useState<HoveredState | null>(null);

  const cx = size / 2;
  const cy = size / 2;
  const r = (size - strokeWidth) / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const activeSegments = segments.filter((s) => s.value > 0);

  const donutRootVars = assignInlineVars({ [donutSizeVar]: `${size}px` });

  let cumulativeFraction = 0;

  const gapFraction = activeSegments.length > 1 ? SEGMENT_GAP_PX / (2 * Math.PI * r) : 0;

  const arcs =
    total > 0
      ? activeSegments.map((seg, i) => {
          const fraction = seg.value / total;
          const startFraction = cumulativeFraction;
          const endFraction = cumulativeFraction + fraction;
          const midFraction = startFraction + fraction / 2;
          cumulativeFraction = endFraction;

          const pad = Math.min(gapFraction / 2, fraction / 4);

          const midAngleRad = midFraction * 2 * Math.PI - Math.PI / 2;
          const tooltipR = r + strokeWidth / 2 + TOOLTIP_GAP;
          const anchor = polarToCartesian(cx, cy, tooltipR, midAngleRad);

          return (
            <path
              key={i}
              data-testid="donut-arc"
              aria-label={seg.label}
              d={buildArcPath(cx, cy, r, startFraction + pad, endFraction - pad)}
              fill="none"
              stroke={seg.color}
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
              className={arcSegment}
              onMouseEnter={() =>
                setHovered({
                  segment: seg,
                  anchorX: anchor.x,
                  anchorY: anchor.y,
                })
              }
              onMouseLeave={() => setHovered(null)}
            />
          );
        })
      : null;

  return (
    <div className={donutRoot} style={donutRootVars}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        className={svg}
      >
        {total === 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={EMPTY_COLOR}
            strokeWidth={strokeWidth}
          />
        )}
        {arcs}
      </svg>
      <Flex className={centerStack}>
        <Text variant="h1" className={centerTotal}>
          {totalCount.toLocaleString()}
        </Text>
        <Text variant="regular-md" className={centerLabel}>
          {totalLabel}
        </Text>
      </Flex>
      {hovered && <DonutTooltip hovered={hovered} />}
    </div>
  );
};
